import React, { useMemo } from 'react';
import { graphql } from 'gatsby';
import { Layout, Seo } from '@layout';
import {
  HeroSection,
  RegistryAppRatingSection,
  ShopRegistrySection,
  ShopSection,
  RegistryCashFundSection,
  RegistryTopBarSection,
  GuestExperienceSection,
  SupportSection,
  RegistryFaqSection,
  ThankYouNoteSection,
} from '@sections/registry';
import { RecommendProducts } from '@sections/shared/GridView';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import { UserContentSection } from '@sections/registry/UserContentSection';
import { TestimonialsCarousel } from '@sections/registry/components/TestimonialsCarousel';
import { useTranslation } from 'react-i18next';
import { createFaqSchema, createProductListSchema, createSoftwareApplicationSchema, ProductSchemaItem } from '@utils/seo';
import { WW_APP_STORE_URLS } from '@sections/wedding-website/wwAppRating.constants';
import { REGISTRY_HERO_IMAGE_OPTIONS, REGISTRY_HERO_IMAGE_SRC } from '@sections/registry/HeroSection/heroImage.constants';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { addRendition } from '@utils/photoRendition';
import { getRootUrl } from '@utils/urlHelpers.util';

const page = 'registry';

interface RegistryBlogs {
  id: string;
  blogs: {
    link: string;
    title: string;
    excerpt: string;
    mediaLink: string;
  }[];
}

interface BlissRegistryData {
  recommendCatalogHomeSections: {
    sections: {
      paginatedCatalogEntities: {
        edges: RecommendProducts;
      };
    }[];
  };
}

interface RegistryPageData {
  bliss: BlissRegistryData;
  featureBlogs: RegistryBlogs;
  wwAppRating: {
    ratingValue: number;
    ratingCount: number;
  };
}

export const query = graphql`
  {
    wwAppRating {
      ratingValue
      ratingCount
    }
    bliss {
      recommendCatalogHomeSections {
        sections(offset: 1, limit: 1) {
          paginatedCatalogEntities(first: 20) {
            edges {
              node {
                ... on Bliss_ProductCatalogEntityItemGroup {
                  catalogItemGroupData {
                    tileViewItem {
                      id
                      title
                      brand {
                        name
                        publicUrlPath
                      }
                      price {
                        currency {
                          code
                          places
                        }
                        floatingPointDecimalString
                        valueInMinorUnits
                      }
                      publicUrlPath
                      photos {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    featureBlogs {
      id
      blogs {
        link
        title
        excerpt
        mediaLink
      }
    }
  }
`;

const Registry = ({ data }: { data: RegistryPageData }) => {
  const { t } = useTranslation();

  const products = data.bliss.recommendCatalogHomeSections.sections[0].paginatedCatalogEntities.edges;

  const { ratingCount } = data.wwAppRating;
  // Round once so the visible rating badge and the JSON-LD AggregateRating use the same value
  // (Google requires AggregateRating markup to match the rating shown on the page).
  const roundedRatingValue = Math.round(data.wwAppRating.ratingValue * 10) / 10;

  const productSchemaItems: ProductSchemaItem[] = useMemo(
    () =>
      // only 12 items are shown in the shop section
      products.slice(0, 12).map((p) => {
        const item = p.node.catalogItemGroupData.tileViewItem;
        const imageUrl = item.photos?.[0]?.url ?? '';

        return {
          name: item.title,
          image: addRendition({ url: imageUrl, renditionSize: 'medium', isProduct: true }),
          description: item.title,
          url: getRootUrl(item.publicUrlPath),
          brand: item.brand?.name,
          ...(item.price && {
            offer: {
              price: item.price.floatingPointDecimalString,
              priceCurrency: item.price.currency.code,
            },
          }),
        };
      }),
    [products]
  );

  return (
    <Layout transparentHeaderButtonVariant="primeWeddingWebsites" headerScrollThresholdVh={35} page={page}>
      <Seo
        title={t('registry:seo.title')}
        canonicalURL={'https://withjoy.com/registry/'}
        description={t('registry:seo.content')}
        ogImage={'common/meta_share/registry.jpg'}
        preloadLinks={[
          {
            rel: 'preload',
            // Must stay byte-identical to the URL CloudImage requests, hence the shared constants.
            href: cloudAssetUrl(REGISTRY_HERO_IMAGE_SRC, REGISTRY_HERO_IMAGE_OPTIONS),
            as: 'image',
            fetchpriority: 'high',
          },
        ]}
        createSchema={(pageUrl) => [
          createFaqSchema(pageUrl, [...t('faq:questions.registry'), ...t('faq:questions.general')]),
          ...createProductListSchema(pageUrl, t('registry:productListName'), productSchemaItems, { includeShippingAndReturns: true }),
          createSoftwareApplicationSchema(pageUrl, {
            name: 'Joy – Wedding Planner & Website',
            applicationCategory: 'LifestyleApplication',
            operatingSystem: 'iOS, Android',
            sameAs: WW_APP_STORE_URLS,
            aggregateRating: {
              ratingValue: roundedRatingValue,
              ratingCount,
            },
          }),
        ]}
      />
      <Box style={{ background: 'linear-gradient(180deg, #FFF 0%, #FFF 84.5%, #F5F5F7 100%)' }}>
        <RegistryTopBarSection />
        <HeroSection />
        <ShopRegistrySection />
      </Box>
      <Flex flexDirection="column" alignItems="center" style={{ backgroundColor: '#F5F5F7' }}>
        <ShopSection products={data.bliss.recommendCatalogHomeSections.sections[0].paginatedCatalogEntities.edges} />
        <RegistryCashFundSection />
        <GuestExperienceSection />
        <ThankYouNoteSection />
        <SupportSection />
        <RegistryFaqSection questionNodes={[...t('faq:questions.registry'), ...t('faq:questions.general')]} />
      </Flex>
      <RegistryAppRatingSection ratingValue={roundedRatingValue} ratingCount={ratingCount} />
      <UserContentSection />
      <TestimonialsCarousel testimonialsData={t('testimonials:registry')} />
    </Layout>
  );
};

export default Registry;
